import { AnimatedChevron, Icon, IconName } from '@ardoq/icons';
import {
  StepContainer,
  Heading,
  StepIconWrapper,
  SubHeading,
  StrikeThroughLine,
} from './atoms';
import { stepColorMap } from './utils';
import { FocusState, StepState, StrikethroughLocation } from './types';
import { SubStepProps } from './SubStep';
import React from 'react';
import { FlexBox, Stack } from '@ardoq/layout';
import { Color } from '@ardoq/design-tokens';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';

export type StepProps = {
  heading: string | React.ReactNode;
  subheading?: string;
  state: StepState;
  expanded?: boolean;
  onExpand?: (expanded: boolean) => void;
  stepNumber?: number;
  isSelected?: boolean;
  isDisabled?: boolean;
  orientation?: 'vertical' | 'horizontal';
  isLast?: boolean;
  strikeThroughLocation?: StrikethroughLocation;
  children?:
    | React.ReactElement<SubStepProps>[]
    | React.ReactElement<SubStepProps>;
  onPress?: VoidFunction;
  icon?: React.ReactNode;
  color?: Record<FocusState, Color>;
  /** Ref to the step's root element. */
  ref?: React.Ref<HTMLDivElement>;
  /**
   * Optional locale for translating the step's internal aria-description
   * ("Step is complete" / "Step is incomplete"). Falls back to English
   * when omitted.
   */
  i18nLocale?: I18nLocale;
};

function getIcon(state: StepState, stepNumber?: number) {
  if (state === StepState.DONE) {
    return <Icon iconName={IconName.CHECK_MARK} iconSize={16} />;
  }
  if (state === StepState.ERROR) {
    return <Icon iconName={IconName.CLOSE} iconSize={16} />;
  }
  if (state === StepState.MODIFIED) {
    return <Icon iconName={IconName.EDIT} iconSize={16} />;
  }

  return <div>{stepNumber}</div>;
}

export function Step({
  heading,
  subheading,
  expanded,
  onExpand,
  state,
  isSelected,
  isDisabled,
  stepNumber,
  isLast = false,
  onPress,
  icon,
  color,
  ref,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  ...rest
}: StepProps) {
  const focusState = isSelected ? 'selected' : 'initial';
  const strikeThroughLocation =
    rest.orientation === 'vertical' ? rest.strikeThroughLocation : undefined;
  const children = rest.orientation === 'vertical' ? rest.children : undefined;
  const orientation = rest.orientation || 'horizontal';
  const isInteractive = Boolean(onPress);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onPress?.();
  };
  const ariaDescription =
    state === StepState.DONE
      ? t(i18nLocale, 'steppers:stepIsComplete', 'Step is complete')
      : t(i18nLocale, 'steppers:stepIsIncomplete', 'Step is incomplete');
  return (
    <>
      <StepContainer
        ref={ref}
        aria-description={isInteractive ? ariaDescription : undefined}
        data-testid={typeof heading === 'string' ? heading : undefined}
        aria-label={
          isInteractive && typeof heading === 'string' ? heading : undefined
        }
        aria-disabled={isDisabled}
        role={isInteractive ? 'button' : undefined}
        $borderColor={
          isDisabled
            ? 'borderDisabled'
            : color?.[focusState] || stepColorMap[state][focusState]
        }
        align="start"
        $isDisabled={isDisabled}
        $isSelected={isSelected}
        $isInteractive={isInteractive}
        $hasBorder={orientation === 'horizontal'}
        $rounded={orientation === 'vertical'}
        tabIndex={isInteractive ? 0 : undefined}
        onKeyDown={isInteractive && !isDisabled ? handleKeyDown : undefined}
        onClick={!isDisabled && onPress ? onPress : undefined}
      >
        {orientation === 'vertical' && strikeThroughLocation && (
          <StrikeThroughLine
            $location={strikeThroughLocation}
            $color={
              isDisabled
                ? 'borderActionDisabled'
                : color?.[focusState] || stepColorMap[state][focusState]
            }
          />
        )}
        <StepIconWrapper
          $color={
            isDisabled
              ? 'iconActionDisabled'
              : color?.[focusState] || stepColorMap[state][focusState]
          }
          $isFilled={focusState === 'selected'}
        >
          {icon || getIcon(state, stepNumber)}
        </StepIconWrapper>
        <Stack flex={1}>
          <Heading>{heading}</Heading>
          {subheading && (
            <SubHeading $isDisabled={isDisabled}>{subheading}</SubHeading>
          )}
        </Stack>

        {/* Icon for drop down */}
        {Boolean(children) && (
          <FlexBox justify="flex-end" style={{ alignSelf: 'center' }}>
            <AnimatedChevron
              $isUp={expanded}
              deprecated_onClick={event => {
                event.stopPropagation();
                onExpand?.(!expanded);
              }}
            />
          </FlexBox>
        )}
      </StepContainer>
      {expanded && children && (
        <>
          {Array.isArray(children) ? (
            children.map((child, index) => (
              <AddStrickThoughLocation
                isLast={isLast && children.length - 1 === index}
                key={index}
                child={child}
              />
            ))
          ) : (
            <AddStrickThoughLocation isLast={isLast} child={children} />
          )}
        </>
      )}
    </>
  );
}

/**
 * @param child: React.ReactElement<SubStepProps> is a child of type SubStepProps, we will add a strikethough location to it
 * @returns wrapped child with strikethoughLocation if it is not provided
 */

function AddStrickThoughLocation({
  child,
  isLast,
}: {
  child: React.ReactElement<SubStepProps>;
  isLast: boolean;
}) {
  const strikeThroughLocation = isLast ? 'top' : 'both';
  return !child.props.strikeThroughLocation
    ? React.cloneElement(child, {
        strikeThroughLocation,
      })
    : child;
}
